'use client'

/* eslint-disable @typescript-eslint/no-floating-promises */
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@utils/utils'
import { saveRecentSearch, getRecentSearches } from '@helpers/driveSearchHelpers'
import { searchBlogs, searchCars } from '@services/driveSearchService'
import { SearchComponentProps, SearchState, SearchType } from '@interfaces/header'
import { segmentEventCommonPropFuncAckoDrive } from '@utils/segmentProperties'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import { getSegmentKey } from '@utils/AckoDriveNewKey'
import logger from '@utils/logger'
import SearchInput from './SearchInput'
import SearchTabs from './SearchTabs'
import RecentSearches from './RecentSearches'
import SearchResults from './SearchResults'
import SearchTrigger from './SearchTrigger'
import styles from './SearchComponent.module.scss'

const SearchComponent = ({
  variant = 'desktop',
  initialTab = 'cars',
  initialQuery = '',
  onBack,
  isStandalone = false,
  className,
}: SearchComponentProps) => {
  const router = useRouter()
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [city, setCity] = useState<string>('Bangalore')
  const envConfig = useEnvVariablesClientConfig()
  const SEGMENT_WRITE_KEY = getSegmentKey(envConfig?.ACKO_ENV)

  const [state, setState] = useState<SearchState>({
    searchQuery: initialQuery,
    activeTab: initialTab,
    searchResults: null,
    isLoading: false,
    isDropdownOpen: false,
    isFocused: false,
  })

  const [recentSearches, setRecentSearches] = useState<any[]>([])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedCity = localStorage.getItem('city') || 'Bangalore'
      setCity(storedCity)
    }
  }, [])

  useEffect(() => {
    const loadRecentSearches = () => {
      const allRecent = getRecentSearches()
      setRecentSearches(allRecent)
    }

    loadRecentSearches()
  }, [])

  useEffect(() => {
    const performSearch = async () => {
      const query = state.searchQuery.trim()

      if (
        !state.searchResults ||
        (state.activeTab === 'cars' && !state.searchResults.carsData?.length) ||
        (state.activeTab === 'blogs' && !state.searchResults.blogsData?.length)
      ) {
        setState(prev => ({ ...prev, isLoading: true }))
      }

      try {
        if (query) {
          const [carResponse, blogResponse] = await Promise.all([
            searchCars(query, city),
            searchBlogs(query),
          ])

          setState(prev => ({
            ...prev,
            searchResults: { carsData: carResponse, blogsData: blogResponse },
            isLoading: false,
          }))
        } else if (state.activeTab === 'cars') {
          const carResponse = await searchCars('Hyundai', city)
          setState(prev => ({
            ...prev,
            searchResults: { carsData: carResponse, blogsData: prev.searchResults?.blogsData || [] },
            isLoading: false,
          }))
        } else {
          const blogResponse = await searchBlogs('')
          setState(prev => ({
            ...prev,
            searchResults: { carsData: prev.searchResults?.carsData || [], blogsData: blogResponse },
            isLoading: false,
          }))
        }
      } catch (error) {
        logger.error('Error fetching search results:', error)
        setState(prev => ({
          ...prev,
          searchResults: { carsData: [], blogsData: [] },
          isLoading: false,
        }))
      }
    }

    const debounceTimer = setTimeout(
      () => {
        void performSearch()
      },
      state.searchQuery.trim() ? 300 : 0
    )

    return () => clearTimeout(debounceTimer)
  }, [state.searchQuery, state.activeTab, city])

  useEffect(() => {
    if (variant === 'mobile' && isStandalone && inputRef.current) {
      inputRef.current.focus()
    }
  }, [variant, isStandalone])

  useEffect(() => {
    if (variant === 'desktop') {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node) &&
          !inputRef.current?.contains(event.target as Node)
        ) {
          setState(prev => ({ ...prev, isDropdownOpen: false, isFocused: false }))
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [variant])

  const handleSearch = (query: string) => {
    setState(prev => ({ ...prev, searchQuery: query }))
    if (variant === 'desktop') {
      setState(prev => ({ ...prev, isDropdownOpen: true }))
    }
  }

  const handleTabChange = (tab: SearchType) => {
    setState(prev => ({ ...prev, activeTab: tab }))
  }

  const handleClear = () => {
    setState(prev => ({ ...prev, searchQuery: '' }))
    inputRef.current?.focus()
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      router.back()
    }
  }

  const handleItemClick = (item: any) => {
    const isBrandCollection = (item: any) =>
      item.name?.toLowerCase().includes('all ') || item.name?.toLowerCase().includes(' cars') || !item.model_name

    let url: string

    if (state.activeTab === 'cars') {
      url = isBrandCollection(item) ? `/collection/${item.slug}+cars` : `/cars/${item.slug}`
    } else {
      url = item.slug
    }

    if (state.searchQuery.trim()) {
      saveRecentSearch(state.searchQuery.trim(), state.activeTab)
      const allRecent = getRecentSearches()
      setRecentSearches(allRecent)
    }
    const eventProperties = segmentEventCommonPropFuncAckoDrive({ pageType: '' })
    if (state.activeTab === 'cars') {
      trackEvent(
        'action',
        {
          ...eventProperties,
          action_type: 'tap_btn',
          action_name: 'tap_btn_search_variant_details',
          search_navigation: url,
          search_query: state.searchQuery,
          city_name: '',
        },
        SEGMENT_WRITE_KEY
      )
    } else {
      trackEvent(
        'action',
        {
          ...eventProperties,
          action_type: 'tap_btn',
          action_name: 'tap_btn_search_blogs',
          search_navigation: url,
          search_query: state.searchQuery,
          city_name: '',
        },
        SEGMENT_WRITE_KEY
      )
    }

    router.push(url)

    if (variant === 'desktop') {
      setState(prev => ({ ...prev, isDropdownOpen: false }))
    }

    setState(prev => ({
      ...prev,
      isFocused: false,
    }))
  }

  const handleRecentSearchClick = (searchTerm: string, searchType: SearchType) => {
    setState(prev => ({
      ...prev,
      searchQuery: searchTerm,
      activeTab: searchType,
    }))
    if (inputRef.current) {
      inputRef.current.value = searchTerm
    }
  }

  const handleInputFocus = () => {
    const eventProperties = segmentEventCommonPropFuncAckoDrive({ pageType: '' })
    trackEvent(
      'action',
      {
        ...eventProperties,
        action_type: 'tap_btn',
        action_name: 'tap_btn_search',
        city_name: '',
      },
      SEGMENT_WRITE_KEY
    )
    setState(prev => ({
      ...prev,
      isDropdownOpen: variant === 'desktop',
      isFocused: true,
    }))
  }

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && state.searchQuery.trim()) {
      saveRecentSearch(state.searchQuery.trim(), state.activeTab)
      const allRecent = getRecentSearches()
      setRecentSearches(allRecent)
    }
  }

  if (variant === 'mobile' && !isStandalone) {
    return <SearchTrigger onClick={() => router.push('/search')} className={className} />
  }

  if (variant === 'mobile') {
    return (
      <div className={cn(styles.searchContainer, styles.mobile, className)}>
        <div className={styles.searchHeader}>
          <div className={styles.searchBar}>
            <SearchInput
              variant={variant}
              searchQuery={state.searchQuery}
              onSearch={handleSearch}
              onClear={handleClear}
              onBack={handleBack}
              onFocus={handleInputFocus}
              onKeyPress={handleKeyPress}
              isFocused={state.isFocused}
              inputRef={inputRef}
            />
          </div>
        </div>
        <RecentSearches
          recentSearches={recentSearches}
          onRecentSearchClick={handleRecentSearchClick}
          searchQuery={state.searchQuery}
          variant={variant}
        />
        <SearchTabs activeTab={state.activeTab} onTabChange={handleTabChange} variant={variant} />
        <div className={styles.searchResults}>
          <SearchResults
            searchResults={state.searchResults}
            searchQuery={state.searchQuery}
            activeTab={state.activeTab}
            onItemClick={handleItemClick}
            variant={variant}
            isLoading={state.isLoading}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={cn(styles.searchContainer, styles.desktop, className)}>
      <div className={cn(styles.searchBar, state.isFocused && styles.searchBarExpanded)}>
        <SearchInput
          variant={variant}
          searchQuery={state.searchQuery}
          onSearch={handleSearch}
          onClear={handleClear}
          onFocus={handleInputFocus}
          onKeyPress={handleKeyPress}
          isFocused={state.isFocused}
          inputRef={inputRef}
        />
      </div>

      {state.isDropdownOpen && (
        <div ref={dropdownRef} className={styles.searchDropdown}>
          <RecentSearches
            recentSearches={recentSearches}
            onRecentSearchClick={handleRecentSearchClick}
            searchQuery={state.searchQuery}
            variant={variant}
          />

          <SearchTabs activeTab={state.activeTab} onTabChange={handleTabChange} variant={variant} />

          <div className={styles.searchResults}>
            <SearchResults
              searchResults={state.searchResults}
              searchQuery={state.searchQuery}
              activeTab={state.activeTab}
              onItemClick={handleItemClick}
              variant={variant}
              isLoading={state.isLoading}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default SearchComponent
